import React from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { CollapsibleSection } from 'cpool-components/general/CollapsibleSection';
import { Checkbox } from 'cpool-components/input/Checkbox';
import { AGGREGATE_NAMES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import DocumentEntry from '~/features/leadDetails/components/Overview/DocumentCenter/Demands/Documents/DocumentEntry';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type {
    SchemaDemandedDocumentEntry,
    SchemaDemandedDocumentsKeys,
} from '~/features/leadDetails/schemas/demandedDocuments.schema';

type Props = {
    category: SchemaDemandedDocumentsKeys;
    title: string;
    documents: SchemaDemandedDocumentEntry[];
    update: (path: SchemaDemandedDocumentsKeys, name: string, requested: boolean) => void;
    isExpandable?: boolean;
};

const DefaultCategory: React.FC<Props> = ({ category, documents, isExpandable = false, title, update }) => {
    const { t } = useTranslation();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const disabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].demandDocuments;

    const allSelected = documents.length > 0 && documents.every((doc) => doc.requested);

    const handleToggleAll = () => {
        const newValue = !allSelected;
        documents.forEach((doc) => {
            update(category, doc.name, newValue);
        });
    };

    const documentsContent = (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {documents.map(({ name, requested }) => (
                <DocumentEntry
                    update={update}
                    document={name}
                    category={category}
                    requested={requested}
                    key={name}
                    disabled={disabled}
                />
            ))}
        </div>
    );

    if (isExpandable) {
        return (
            <CollapsibleSection
                bgClassNames="rounded-md border border-grey-stroke bg-surface-light px-4 py-3"
                title={title}
                titleClassNames="text-sm font-medium"
            >
                <div className="mt-2.5 flex flex-col gap-3 p-0.5">
                    <Checkbox
                        id={`${category}-toggle-all`}
                        checked={allSelected}
                        onChange={handleToggleAll}
                        disabled={disabled}
                        label={allSelected ? t('DOCUMENT_DEMANDER.DESELECT_ALL') : t('DOCUMENT_DEMANDER.SELECT_ALL')}
                    />
                    {documentsContent}
                </div>
            </CollapsibleSection>
        );
    }

    return (
        <div className="flex flex-col gap-3 rounded-md border border-grey-stroke bg-surface-light px-4 py-3">
            <p className="text-sm font-medium text-midnight">{title}</p>
            {documentsContent}
        </div>
    );
};

export default DefaultCategory;
